import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Calendar, Clock, MapPin, ArrowRight, CheckCircle, XCircle, ListChecks,
    UserPlus, Receipt, CalendarBlank, Stethoscope,
} from "@phosphor-icons/react";


/* Compact appointment-type lookup. We don't need the full APPT_TYPES table
   here — just enough to render a friendly label for each row. */
const TYPE_LABELS = {
    consultation:    "Consultation",
    scan:            "Scan / impression",
    delivery:        "Aligner delivery",
    adjustment:      "Adjustment / review",
    refinement_scan: "Refinement scan",
    retainer_check:  "Retainer check",
    emergency:       "Emergency / repair",
    video_consult:   "Video consultation",
    // legacy fall-throughs
    first_fitting:   "First fitting",
    review:          "Review",
    refinement:      "Refinement",
    general:         "General visit",
};
const labelFor = (t) => TYPE_LABELS[t] || (t ? t.replace(/_/g, " ") : "Appointment");

const STATUS_TONE = {
    scheduled:      { bg: "bg-blue-50",    text: "text-blue-800",    border: "border-blue-200",    label: "Scheduled" },
    confirmed:      { bg: "bg-cyan-50",    text: "text-cyan-800",    border: "border-cyan-200",    label: "Confirmed" },
    arrived:        { bg: "bg-amber-50",   text: "text-amber-900",   border: "border-amber-200",   label: "Arrived" },
    completed:      { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", label: "Completed" },
    cancelled:      { bg: "bg-rose-50",    text: "text-rose-800",    border: "border-rose-200",    label: "Cancelled" },
    no_show:        { bg: "bg-slate-100",  text: "text-slate-700",   border: "border-slate-200",   label: "No-show" },
};

function fmtDateTime(date, time) {
    if (!date) return "—";
    try {
        const d = new Date(`${date}T${time || "00:00"}:00`);
        return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) + (time ? ` · ${time}` : "");
    } catch {
        return `${date}${time ? " · " + time : ""}`;
    }
}

function relativeDays(date) {
    if (!date) return "";
    try {
        const ms = new Date(`${date}T00:00:00`).getTime() - new Date().setHours(0, 0, 0, 0);
        const d = Math.round(ms / 86400000);
        if (d === 0) return "Today";
        if (d === 1) return "Tomorrow";
        if (d === -1) return "Yesterday";
        return d > 0 ? `in ${d} days` : `${Math.abs(d)} days ago`;
    } catch {
        return "";
    }
}


/**
 * AppointmentsTab — doctor's Patient Profile → Appointments tab.
 *
 * Pulls from GET /api/doctor/patients/{patient_id}/appointments and renders:
 *   - 4 small stat cards (Total · Upcoming · Completed · No-shows)
 *   - "Book appointment" CTA → routes to /doctor/appointments?patient_id=…
 *   - Two sections: Upcoming (asc by date) + Past (desc by date)
 */
export function AppointmentsTab({ patientId, patientName }) {
    const navigate = useNavigate();
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!patientId) return;
        api.get(`/doctor/patients/${patientId}/appointments`)
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [patientId]);

    const split = useMemo(() => {
        if (!items) return { upcoming: [], past: [] };
        const todayIso = new Date().toISOString().slice(0, 10);
        const upcoming = items
            .filter((a) => (a.date || "") >= todayIso && !["completed", "cancelled", "no_show"].includes(a.status))
            .sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")));
        const past = items
            .filter((a) => !upcoming.includes(a))
            .sort((a, b) => (b.date + (b.time || "")).localeCompare(a.date + (a.time || "")));
        return { upcoming, past };
    }, [items]);

    const stats = useMemo(() => {
        const list = items || [];
        return {
            total: list.length,
            upcoming: split.upcoming.length,
            completed: list.filter((a) => a.status === "completed").length,
            no_show: list.filter((a) => a.status === "no_show").length,
        };
    }, [items, split]);

    return (
        <div className="space-y-5" data-testid="patient-appointments-tab">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StatCard label="Total visits"  value={loading ? "—" : stats.total}     Icon={ListChecks}    tone="primary" />
                <StatCard label="Upcoming"      value={loading ? "—" : stats.upcoming}  Icon={Calendar}      tone="cyan" highlight={stats.upcoming > 0} />
                <StatCard label="Completed"     value={loading ? "—" : stats.completed} Icon={CheckCircle}   tone="emerald" />
                <StatCard label="No-shows"      value={loading ? "—" : stats.no_show}   Icon={XCircle}       tone={stats.no_show > 0 ? "rose" : "neutral"} />
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-muted-foreground">
                    {patientName ? `Appointment history for ${patientName.split(/\s+/)[0]}` : "Appointment history"}
                </div>
                <Button
                    onClick={() => navigate(`/doctor/appointments?patient_id=${patientId}`)}
                    className="rounded-full"
                    data-testid="patient-appts-book-btn"
                >
                    <UserPlus size={14} weight="fill" className="mr-1.5" /> Book appointment
                </Button>
            </div>

            {/* Sections */}
            {loading ? (
                <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div>
            ) : items.length === 0 ? (
                <EmptyState onBook={() => navigate(`/doctor/appointments?patient_id=${patientId}`)} />
            ) : (
                <>
                    {split.upcoming.length > 0 && (
                        <Section title="Upcoming" subtitle={`${split.upcoming.length} scheduled`} rows={split.upcoming} navigate={navigate} />
                    )}
                    {split.past.length > 0 && (
                        <Section title="Past visits" subtitle={`${split.past.length} on record`} rows={split.past} navigate={navigate} muted />
                    )}
                </>
            )}
        </div>
    );
}


function StatCard({ label, value, Icon, tone, highlight }) {
    const tones = {
        primary: { border: "border-primary/20",  bg: "bg-primary/5",   icon: "text-primary" },
        cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",     icon: "text-cyan-700" },
        emerald: { border: "border-emerald-200", bg: "bg-emerald-50",  icon: "text-emerald-700" },
        rose:    { border: "border-rose-200",    bg: "bg-rose-50",     icon: "text-rose-700" },
        neutral: { border: "border-border",      bg: "bg-card",        icon: "text-muted-foreground" },
    };
    const c = tones[tone] || tones.neutral;
    return (
        <div className={`rounded-xl border ${c.border} ${c.bg} p-3 ${highlight ? "ring-1 ring-cyan-300" : ""}`} data-testid={`patient-appts-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-1.5 mb-1.5">
                <Icon size={13} weight="fill" className={c.icon} />
                <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-foreground/70">{label}</div>
            </div>
            <div className="font-display text-2xl font-bold tabular-nums leading-none">{value}</div>
        </div>
    );
}


function Section({ title, subtitle, rows, navigate, muted = false }) {
    return (
        <div>
            <div className="flex items-end justify-between mb-2 px-1">
                <div>
                    <h3 className="font-display text-base font-bold tracking-tight">{title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
                </div>
            </div>
            <div className={`space-y-2 ${muted ? "opacity-90" : ""}`}>
                {rows.map((a) => <Row key={a.id} a={a} navigate={navigate} />)}
            </div>
        </div>
    );
}


function Row({ a, navigate }) {
    const status = STATUS_TONE[a.status] || STATUS_TONE.scheduled;
    const rel = relativeDays(a.date);
    return (
        <div
            className="bg-card border border-border rounded-xl p-3.5 hover:border-primary/40 hover:shadow-sm transition-all"
            data-testid={`patient-appt-row-${a.id}`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Calendar size={16} weight="duotone" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">{labelFor(a.type)}</span>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${status.bg} ${status.text} ${status.border}`}>
                                {status.label}
                            </span>
                            {a.case_code && (
                                <button
                                    type="button"
                                    onClick={() => navigate(`/doctor/cases/${a.case_id}`)}
                                    className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground hover:text-primary hover:bg-primary/10"
                                    data-testid={`patient-appt-case-link-${a.id}`}
                                    title="Open this aligner case"
                                >
                                    For case · {a.case_code}
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                                <Clock size={11} /> {fmtDateTime(a.date, a.time)}
                            </span>
                            {rel && <span className="font-semibold text-foreground/80">{rel}</span>}
                            {a.location_name && (
                                <span className="inline-flex items-center gap-1">
                                    <MapPin size={11} /> {a.location_name}
                                </span>
                            )}
                            {a.staff_user_name && (
                                <span className="inline-flex items-center gap-1">
                                    <Stethoscope size={11} /> {a.staff_user_name}
                                </span>
                            )}
                        </div>
                        {a.notes && (
                            <p className="text-xs text-foreground/80 mt-2 italic line-clamp-2">"{a.notes}"</p>
                        )}
                        {a.cancel_reason && a.status === "cancelled" && (
                            <p className="text-xs text-rose-700 mt-2">Cancelled — {a.cancel_reason}</p>
                        )}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => navigate(`/doctor/appointments?id=${a.id}`)}
                    className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 shrink-0"
                    title="Open in Appointments"
                    data-testid={`patient-appt-open-${a.id}`}
                >
                    Open <ArrowRight size={11} />
                </button>
            </div>
        </div>
    );
}


function EmptyState({ onBook }) {
    return (
        <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center" data-testid="patient-appts-empty">
            <CalendarBlank size={32} weight="duotone" className="text-muted-foreground mx-auto" />
            <div className="text-sm font-semibold text-foreground mt-3">No appointments yet</div>
            <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
                When you book a consultation, scan, fitting, or review with this patient it&apos;ll appear here. Use the &ldquo;Book appointment&rdquo; button above to schedule the first visit.
            </div>
        </div>
    );
}

export default AppointmentsTab;
